import { useId } from 'react';
import { ASPECT_RATIO_OPTIONS, type AspectRatioValue } from './constants';
import Icon from '@/components/atoms/Icon';
import { classnames } from '@/libs/utils';

type SingleSelectProps = {
  /** Omit or pass false for single select: exactly one ratio is always active. */
  multiple?: false;
  value: AspectRatioValue;
  onChange: (value: AspectRatioValue) => void;
};

type MultiSelectProps = {
  /** Pass true for multi select: tiles toggle independently and none need be active. */
  multiple: true;
  value: AspectRatioValue[];
  onChange: (value: AspectRatioValue[]) => void;
};

export type AspectRatioTilesProps = (SingleSelectProps | MultiSelectProps) & {
  label?: string;
  className?: string;
  /** Tailwind gap between tiles, tightened by callers in narrow containers. */
  tileGapClassName?: string;
  /** Caption typography, replacing the default so callers can match their surrounding section headings. */
  labelClassName?: string;
};

/**
 * Aspect ratio thumbnail tiles, shared by the New Content upload flow (multi select)
 * and the highlight reel generation panel (single select).
 */
export default function AspectRatioTiles(props: AspectRatioTilesProps): JSX.Element {
  const { label, className, tileGapClassName = 'gap-4', labelClassName = 'text-sm font-medium text-slate-700' } = props;
  const labelId = useId();

  function isSelected(value: AspectRatioValue): boolean {
    return props.multiple ? props.value.includes(value) : props.value === value;
  }

  function handleSelect(value: AspectRatioValue): void {
    if (props.multiple) {
      props.onChange(props.value.includes(value) ? props.value.filter(r => r !== value) : [...props.value, value]);
      return;
    }
    props.onChange(value);
  }

  return (
    <div className={className}>
      {/*
        The tiles are a group of toggle buttons rather than a single form control, so the caption is
        a span tied to the group via aria-labelledby — a <label> would have nothing to point at.
      */}
      {!!label && (
        <span id={labelId} className={classnames('mb-2 block', labelClassName)}>
          {label}
        </span>
      )}
      <div role="group" aria-labelledby={label ? labelId : undefined} className={classnames('flex', tileGapClassName)}>
        {ASPECT_RATIO_OPTIONS.map(option => {
          const selected = isSelected(option.value);
          return (
            <button
              key={option.value}
              type="button"
              data-testid={`aspect-ratio-option-${option.value}`}
              aria-pressed={selected}
              onClick={() => handleSelect(option.value)}
              className={classnames(
                'relative flex flex-1 flex-col items-center gap-1 rounded-2xl border p-4 text-center transition-colors',
                selected ? 'border-theme-border bg-orange-50' : 'border-card-border bg-white hover:bg-slate-50'
              )}
            >
              {selected && <Icon name="IconCheck" size={16} className="absolute right-2 top-2 text-theme-border" />}
              <Icon name={option.icon} size={32} stroke={1} className="text-black" />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-black">{option.ratioLabel}</span>
                <span className="text-xs text-slate-400">{option.subLabel}</span>
              </div>
              <div className="mt-0.5 flex gap-1">
                {option.platformIcons.map(platformIcon => (
                  <Icon key={platformIcon} name={platformIcon} size={16} className="text-slate-300" />
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
